import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Copy, Trash2, ImageIcon, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Admin() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/media`);
      setFiles(res.data);
    } catch {
      toast.error("Could not load your media library.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF images are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded successfully.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const copyLink = (url) => {
    const full = `${BACKEND}${url}`;
    navigator.clipboard.writeText(full);
    toast.success("Image link copied to clipboard.");
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`);
      setFiles((f) => f.filter((x) => x.id !== id));
      toast.success("Image removed from library.");
    } catch {
      toast.error("Could not delete this image.");
    }
  };

  return (
    <div data-testid="admin-page">
      <PageHero
        overline="Media Library"
        lines={["Upload & Manage", "Your Farm Photos"]}
        subtitle="Upload your own polyhouse and hydroponic images, then copy the link to use anywhere on the site."
        testId="admin-hero"
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24">
          {/* Dropzone */}
          <div
            data-testid="admin-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              doUpload(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer border border-dashed p-12 md:p-20 text-center transition-colors duration-300 ${
              dragOver ? "border-primary bg-accent/40" : "border-border hover:border-primary/60"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              data-testid="admin-file-input"
              onChange={(e) => doUpload(e.target.files)}
            />
            {uploading ? (
              <Loader2 className="w-10 h-10 text-primary mx-auto animate-spin" />
            ) : (
              <UploadCloud className="w-10 h-10 text-primary mx-auto" strokeWidth={1.5} />
            )}
            <p className="mt-6 font-clash text-xl md:text-2xl">
              {uploading ? "Uploading..." : "Drop an image here or click to browse"}
            </p>
            <p className="mt-3 overline text-muted-foreground">JPG · PNG · WEBP · GIF · Max 10MB</p>
          </div>

          {/* Gallery */}
          <div className="mt-16">
            <div className="flex items-center justify-between">
              <p className="overline text-primary">Your Library</p>
              <span className="overline text-muted-foreground">{files.length} image(s)</span>
            </div>

            {files.length === 0 ? (
              <div className="mt-10 border border-border p-16 text-center text-muted-foreground">
                <ImageIcon className="w-8 h-8 mx-auto mb-4 opacity-50" />
                No images yet. Upload your first photo above.
              </div>
            ) : (
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
                {files.map((f) => (
                  <div
                    key={f.id}
                    data-testid={`media-item-${f.id}`}
                    className="group border-r border-b border-border"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                      <img
                        src={`${BACKEND}${f.url}`}
                        alt={f.original_filename}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 flex items-center justify-between gap-3">
                      <span className="text-sm text-muted-foreground truncate" title={f.original_filename}>
                        {f.original_filename}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          data-testid={`media-copy-${f.id}`}
                          onClick={() => copyLink(f.url)}
                          className="grid place-items-center w-9 h-9 border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                          title="Copy link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`media-delete-${f.id}`}
                          onClick={() => remove(f.id)}
                          className="grid place-items-center w-9 h-9 border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
